<template>
    <Modal class="switch-user-modal"
           :cancel="logout"
           cancel-label="LOGOUT"
           icon="fa-lock"
           :ok="ok"
           :ok-spinner="loading"
           :blur-backdrop="true"
           size="sm"
           title="SWITCH_USER.TITLE">
        <p class="text-center">
            {{ l('SWITCH_USER.PROMPT') }}
        </p>

        <NumPad ref="num-pad"
                v-model="numPadValue"
                :show-decimal-button="false"
                :show-sign-button="false"
                mask-value
                is-pin-mode
                @ok="ok"
                @clear="onNumPadClear" />

        <ErrorMessage v-if="error"
                      class="mt-1"
                      :error="error" />
    </Modal>
</template>

<script>
import _ from 'lodash';
import ErrorMessage from '@/vue/components/Errors/ErrorMessage';
import EventNames from '@/lib/Events/EventNames';
import Modal from '@/vue/components/Modals/Modal';
import NumPad from '@/vue/components/NumPad/NumPad';
import UserFacingError from '@/libraries/DigiTickets/Errors/UserFacingError';
import { container } from '@/container';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { timeoutPromise } from '@/functions/functions';

export default {
    name: 'SwitchUserModal',

    components: {
        Modal,
        ErrorMessage,
        NumPad,
    },

    mixins: [
        ModalPropsMixin,
    ],

    data() {
        return {
            /**
             * @type {string}
             */
            numPadValue: '',

            /**
             * @type {UserFacingError|Error|null}
             */
            error: null,

            /**
             * @type {boolean}
             */
            loading: false,

            /**
             * Replaced by a real function in created().
             */
            debouncedHandleSwitchUserAuth: () => {},
        };
    },

    created() {
        this.debouncedHandleSwitchUserAuth = _.debounce(this.handleSwitchUserAuth, 100);

        this.deregisterRfidScanListenerFunc = container.$rootScope.$on(
            EventNames.RFID_TAG_SCANNED,
            (event, response) => {
                console.debug('RFID Tag Scanned (Switch User Modal)', response.code);

                // Scanning the RFID will trigger a bunch of keydown events then the enter key.
                // The NumPad is picking up those keydown events, filling its input and then trying to search by PIN.
                // To combat this there is a small timeout in the ok() handler for the numpad above to delay that PIN
                // search triggering.
                // If an RFID tag was scanned we clear the NumPad's input, making the ok() do nothing.
                this.$refs['num-pad'].numPad.clear();

                // Only look for a matching RFID tag, and not a PIN.
                this.debouncedHandleSwitchUserAuth('rfid-tag', response.code);
            },
        );
    },

    beforeUnmount() {
        if (this.deregisterRfidScanListenerFunc) {
            this.deregisterRfidScanListenerFunc();
        }
    },

    methods: {
        getNumPadValue() {
            return this.$refs['num-pad']?.numPad?.getValue();
        },

        async ok() {
            // The timeout is to allow the RFID handler to pickup the keydowns first if appropriate.
            await timeoutPromise(10);

            const pin = this.getNumPadValue();
            if (pin) {
                this.debouncedHandleSwitchUserAuth('pin', String(pin));
            }
        },

        /**
         * @param {UserFacingError|Error|null} error
         */
        setError(error) {
            this.error = error;
        },

        onNumPadClear() {
            this.setError(null);
        },

        logout() {
            container.userService.logout();
        },

        handleSwitchUserAuth(method, enteredValue) {
            if (this.loading) {
                return;
            }

            if (!enteredValue) {
                this.setError(new UserFacingError('No PIN', 'SWITCH_USER.NO_PIN_ERROR'));
                return;
            }

            this.setError(null);
            this.loading = true;

            container.userSwitcher
                .switchUser(method, enteredValue)
                .then(() => {
                    // Switched.
                    this.resolve();
                    this.loading = false;
                })
                .catch((error) => {
                    console.error('Failed to switch user.', error);
                    if (error instanceof Error && !error.isRedirecting) {
                        // Only show the error if we're not redirecting otherwise we show
                        // a flash of an error to the user and it looks like something broke.
                        this.setError(error);
                    }
                    if (!error.isRedirecting) {
                        // Keep the OK button spinning if we're redirecting so the user can see
                        // something is happening.
                        this.loading = false;
                    }

                    // Clear the value in the num pad without clearing the error message.
                    this.$refs['num-pad']?.numPad?.clear();
                });
        },
    },
};
</script>
